import { useEffect, useState } from 'react';
import storage, { LocalData } from 'storage';
import {
	buildVariantUrl,
	getVariantOptions,
	getVariantValue,
	registerVariantOptions,
	resetVariants,
	setVariant,
	subscribeVariants,
} from 'variants';
import { VARIANT_OPTIONS } from 'variants/options';
import * as S from './styles';

// Options are registered up front so the panel is populated before the surface
// under exploration has been opened.
registerVariantOptions(VARIANT_OPTIONS);

type Corner = NonNullable<LocalData['variantsPanelCorner']>;

const CORNER_ORDER: Corner[] = ['br', 'bl', 'tl', 'tr'];

const VariantsPanel = () => {
	const [, bump] = useState(0);
	const [isOpen, setIsOpen] = useState(true);
	const [isCopied, setIsCopied] = useState(false);
	const [corner, setCorner] = useState<Corner>(() => storage.local.variantsPanelCorner ?? 'br');

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	useEffect(() => subscribeVariants(() => bump((n) => n + 1)), []);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onCycleCorner = () => {
		const next = CORNER_ORDER[(CORNER_ORDER.indexOf(corner) + 1) % CORNER_ORDER.length];
		setCorner(next);
		// corner is viewer preference, not experiment state: storage only, never the URL
		const data = storage.local;
		data.variantsPanelCorner = next;
		storage.local = data;
	};

	const onCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(buildVariantUrl());
			setIsCopied(true);
			setTimeout(() => setIsCopied(false), 1200);
		} catch {
			// clipboard unavailable (unfocused document, permissions) — no ✓ shown
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const options = getVariantOptions();
	if (options.length === 0) return null;
	// reserved key: ?variants=panel:0 hides the panel for clean screenshots
	if (getVariantValue('panel') === 0) return null;

	return (
		<S.Base $corner={corner}>
			<S.Header>
				<S.Title>Variants</S.Title>
				<S.Controls>
					<S.Control onClick={onCopyLink} title="Copy link with these variants pinned">
						{isCopied ? '✓' : '⧉'}
					</S.Control>
					<S.Control onClick={resetVariants} title="Reset all variants to defaults">
						↺
					</S.Control>
					<S.Control onClick={onCycleCorner} title="Move panel to another corner">
						◲
					</S.Control>
					<S.Control onClick={() => setIsOpen(!isOpen)} title={isOpen ? 'Collapse' : 'Expand'}>
						{isOpen ? '–' : '+'}
					</S.Control>
				</S.Controls>
			</S.Header>
			{isOpen &&
				options.map((option) => {
					const current = getVariantValue(option.key);
					return (
						<S.Row key={option.key}>
							<S.Label>{option.title}</S.Label>
							<S.Values>
								{option.labels.map((label, index) => (
									<S.Value
										key={label}
										$active={current === index + 1}
										onClick={() => setVariant(option.key, index + 1)}
										title={label}
									>
										{index + 1}
									</S.Value>
								))}
							</S.Values>
							<S.Current>{option.labels[current - 1]}</S.Current>
						</S.Row>
					);
				})}
		</S.Base>
	);
};

export default VariantsPanel;
